import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

export function MarketingNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  const isHero = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const showLight = scrolled || !isHero;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: showLight ? 'rgba(255,255,255,0.95)' : 'transparent',
        backdropFilter: showLight ? 'blur(20px) saturate(1.4)' : 'none',
        borderBottom: showLight ? '1px solid rgba(0,0,0,0.06)' : 'none',
      }}
      data-testid="marketing-navbar">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-8 lg:px-12" style={{ height: '80px' }}>
        <Link to="/" className="flex items-center" data-testid="logo-home-link" style={{ marginLeft: '16px' }}>
          <img
            src={showLight ? '/logos/Kairera.png' : '/logos/Kairera-white.png'}
            alt="Kairera"
            className="w-auto transition-all duration-300"
            style={{ height: '52px' }}
          />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {[
            { label: 'Features', to: '/features' },
            { label: 'How It Works', to: '/how-it-works' },
            { label: 'Pricing', to: '/pricing' },
          ].map(l => (
            <Link key={l.label} to={l.to}
              className="text-[13px] font-semibold transition-colors"
              style={{
                color: location.pathname === l.to
                  ? '#0D9488'
                  : showLight ? '#475569' : '#C4D3EE',
              }}>
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Button onClick={() => navigate('/login')}
            className="h-9 px-5 text-[13px] font-semibold rounded-lg"
            style={{
              background: 'transparent',
              color: showLight ? '#0D1E3A' : '#fff',
              border: showLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.25)',
            }}
            data-testid="nav-login-btn">
            Sign In
          </Button>
          <Button onClick={() => navigate('/register')}
            className="h-9 px-5 text-[13px] font-semibold rounded-lg"
            style={{ background: '#0D9488', color: '#fff' }}
            data-testid="nav-register-btn">
            Get Started Free
          </Button>
        </div>

        <button className="md:hidden" style={{ color: showLight ? '#0D1E3A' : '#fff' }}
          onClick={() => setMobileMenu(!mobileMenu)}>
          {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {mobileMenu && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="md:hidden px-6 pb-4 space-y-2 bg-white border-t border-slate-100">
          {[
            { label: 'Features', to: '/features' },
            { label: 'How It Works', to: '/how-it-works' },
            { label: 'Pricing', to: '/pricing' },
          ].map(l => (
            <Link key={l.label} to={l.to} onClick={() => setMobileMenu(false)}
              className="block py-2 text-sm font-medium" style={{ color: '#475569' }}>{l.label}</Link>
          ))}
          <div className="flex gap-2 pt-2">
            <Button onClick={() => navigate('/login')} className="flex-1 h-10 text-sm font-semibold"
              style={{ background: '#f8fafc', color: '#0D1E3A', border: '1px solid #e2e8f0' }}>Sign In</Button>
            <Button onClick={() => navigate('/register')} className="flex-1 h-10 text-sm font-semibold"
              style={{ background: '#0D9488', color: '#fff' }}>Get Started</Button>
          </div>
        </motion.div>
      )}
    </nav>
  );
}

export function MarketingFooter() {
  return (
    <footer className="py-12" style={{ background: '#070F1D', borderTop: '1px solid rgba(255,255,255,0.04)' }} data-testid="footer">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src="/logos/Kairera-white.png" alt="Kairera" className="w-auto" style={{ height: '44px' }} />
            </Link>
            <p className="text-[11px] leading-relaxed" style={{ color: '#94A3B8' }}>
              Career intelligence for professionals who refuse to leave their future to chance.
            </p>
          </div>
          {[
            { title: 'Product', links: [
              { label: 'Features', to: '/features' },
              { label: 'Pricing', to: '/pricing' },
              { label: 'How It Works', to: '/how-it-works' },
            ]},
            { title: 'Company', links: [
              { label: 'About', to: '#' },
              { label: 'Blog', to: '#' },
              { label: 'Careers', to: '#' },
            ]},
            { title: 'Legal', links: [
              { label: 'Privacy', to: '#' },
              { label: 'Terms', to: '#' },
              { label: 'Security', to: '#' },
            ]},
          ].map(col => (
            <div key={col.title}>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#E2E8F0' }}>{col.title}</h4>
              <ul className="space-y-1.5">
                {col.links.map(l => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-xs transition-colors hover:text-white" style={{ color: '#94A3B8' }}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-between pt-6"
          style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <p className="text-[10px]" style={{ color: '#94A3B8' }}>&copy; {new Date().getFullYear()} Kairera. All rights reserved.</p>
          <p className="text-[10px]" style={{ color: '#94A3B8' }}>Built with intelligence.</p>
        </div>
      </div>
    </footer>
  );
}
